
import { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/contexts/auth";
import { usePermissions } from "@/hooks/use-permissions";
import { toast } from "sonner";
import AuthLogo from "@/components/auth/AuthLogo";
import LoginForm from "@/components/auth/LoginForm";
import SignupForm from "@/components/auth/SignupForm";
import type { LoginFormValues } from "@/components/auth/LoginForm";
import type { SignupFormValues } from "@/components/auth/SignupForm";
import { Mail, CheckCircle } from "lucide-react";

const Auth = () => {
  const location = useLocation();
  const defaultTab = location.state?.defaultTab || "login";
  const [activeTab, setActiveTab] = useState<string>(defaultTab);
  const [authError, setAuthError] = useState<string | null>(null);
  const [signupSuccess, setSignupSuccess] = useState(false);
  const [userEmail, setUserEmail] = useState<string>("");
  const { signIn, signUp, user, isLoading } = useAuth();
  const { isAdmin, isSuperAdmin } = usePermissions();
  const navigate = useNavigate();
  const processedRedirect = useRef(false);

  useEffect(() => {
    // Enhanced URL error parameter handling
    const processUrlErrors = () => {
      const params = new URLSearchParams(window.location.search);
      const hashParams = new URLSearchParams(window.location.hash?.substring(1) || '');
      
      const errorMsg = params.get('error_description') || hashParams.get('error_description');
      const errorCode = params.get('error_code') || hashParams.get('error_code');
      
      if (errorMsg) {
        console.error('Auth error detected in URL:', { 
          errorMsg, 
          errorCode,
          fullUrl: window.location.href
        });
        
        const decodedError = decodeURIComponent(errorMsg);
        setAuthError(decodedError);
        
        // More specific error handling
        if (decodedError.includes('Database error') || errorCode === 'unexpected_failure') {
          toast.error('Sign-up failed: There was a database error processing your registration. Please try again later or contact support.');
        } else if (decodedError.includes('User already exists')) {
          toast.error('An account with this email already exists. Please log in or use a different email.');
        } else {
          toast.error(`Authentication failed: ${decodedError}`);
        }
        
        // Clear URL params to avoid showing the error again on refresh
        if (window.history && window.history.replaceState) {
          window.history.replaceState({}, document.title, '/auth');
        }
      }
    };
    
    processUrlErrors();
  }, [location]);
  
  // Handle user redirection logic separately to avoid race conditions
  useEffect(() => {
    if (user && !processedRedirect.current) {
      processedRedirect.current = true;
      console.log('Auth: User authenticated, redirecting');
      
      if (isAdmin() || isSuperAdmin()) {
        navigate("/admin/dashboard");
      } else {
        const from = location.state?.from?.pathname || "/";
        navigate(from);
      }
    }
  }, [user, isAdmin, isSuperAdmin, navigate, location]);

  const handleLoginSubmit = async (data: LoginFormValues) => {
    setAuthError(null);
    try {
      await signIn(data.email, data.password);
    } catch (error: any) {
      console.error("Login error:", error);
      const errorMessage = error.message || "Failed to sign in";
      setAuthError(errorMessage);
      toast.error(errorMessage);
      throw error;
    }
  };

  const handleSignupSubmit = async (data: SignupFormValues) => {
    setAuthError(null);
    try {
      console.log("Signup form submitted with:", { 
        email: data.email, 
        name: data.name,
        passwordLength: data.password.length 
      });
      
      await signUp(data.email, data.password, { name: data.name });
      
      // Store email and show success state
      setUserEmail(data.email);
      setSignupSuccess(true);
      
      // Show a toast notification as well
      toast.success("Account created! Check your email for verification link.");
    } catch (error: any) {
      console.error("Signup error:", error);
      const errorMessage = error.message || "Failed to create account";
      setAuthError(errorMessage);
      toast.error(errorMessage);
      throw error;
    }
  };

  // Email verification success UI
  const renderSignupSuccess = () => (
    <div className="space-y-6 text-center">
      <div className="flex justify-center">
        <div className="h-16 w-16 bg-green-100 rounded-full flex items-center justify-center">
          <CheckCircle className="h-10 w-10 text-green-600" />
        </div>
      </div>
      
      <div className="space-y-3">
        <h3 className="text-xl font-medium text-gray-900 dark:text-gray-100">
          Check your email
        </h3>
        <p className="text-gray-600 dark:text-gray-400">
          We've sent a verification link to <span className="font-medium">{userEmail}</span>
        </p>
        <p className="text-gray-500 dark:text-gray-500 text-sm">
          Click the link to activate your account and sign in.
        </p>
      </div>
      
      <div className="border-t border-gray-200 dark:border-gray-800 pt-6 mt-6">
        <div className="bg-blue-50 dark:bg-blue-900/30 p-4 rounded-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <Mail className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-blue-700 dark:text-blue-300">
                Don't see the email? Check your spam folder or click below to resend.
              </p>
            </div>
          </div>
        </div>
      </div>
      
      <div className="pt-2">
        <button
          type="button"
          onClick={() => setActiveTab("login")}
          className="text-nation-purple dark:text-purple-400 hover:underline text-sm"
        >
          Go to login page
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-lg shadow">
        <AuthLogo />

        {authError && (
          <div className="p-3 bg-red-100 border border-red-300 text-red-800 rounded-md text-sm">
            {authError}
          </div>
        )}

        <Tabs defaultValue={defaultTab} value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-2 w-full">
            <TabsTrigger value="login">Login</TabsTrigger>
            <TabsTrigger value="signup">Sign Up</TabsTrigger>
          </TabsList>

          <TabsContent value="login">
            <LoginForm onSubmit={handleLoginSubmit} isLoading={isLoading} />
          </TabsContent>

          <TabsContent value="signup">
            {signupSuccess ? (
              renderSignupSuccess()
            ) : (
              <SignupForm onSubmit={handleSignupSubmit} isLoading={isLoading} />
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Auth;
